import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Layers } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Checkbox,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useWaapCacheRules,
  useCreateWaapCacheRule,
  useUpdateWaapCacheRule,
  useToggleWaapCacheRule,
  useDeleteWaapCacheRule,
  type WaapCacheRule,
  type WaapCacheInput,
  type WaapCacheMatch,
} from '@/api/waap';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './CacheControlTab.module.css';

// The public Open-API exposes two fixed match slots: match_url_path /
// match_query_string. The single match row maps to one of these.
const URL_PATH = 'url_path';
const QUERY_STRING = 'query_string';
const FIELDS = [
  { value: URL_PATH, label: 'URL Path' },
  { value: QUERY_STRING, label: 'Query String' },
];
// Cache/redirection use the legacy MatchPattern operator set (SDK types.go:
// MatchPattern.Operator ∈ prefix | regex | equals | suffix). NOT the v2 Match
// startswith/contains/iequals vocabulary — those 400 on the public surface.
const OPERATORS = ['equals', 'prefix', 'suffix', 'regex'];
const VARY_MIMES = ['image/webp', 'image/avif', 'image/jpeg', 'image/jpg', 'image/png'];

// Cache toggles — label + form key + helper text (matches the vendor portal copy).
type ToggleKey =
  | 'enable_x_cache'
  | 'ignore_origin_no_cache'
  | 'ignore_client_no_cache'
  | 'never_cache'
  | 'ignore_query_string';
const TOGGLES: { key: ToggleKey; label: string; hint: string }[] = [
  { key: 'enable_x_cache', label: 'Enable X Cache', hint: 'Responses carry an X-Cache header indicating a cache hit/miss.' },
  { key: 'ignore_origin_no_cache', label: 'Ignore No Cache from Origin Server', hint: 'Cache origin content even when the response is marked no-cache.' },
  { key: 'ignore_client_no_cache', label: 'Ignore No Cache from Client', hint: "Ignore clients' no-cache header(s)." },
  { key: 'never_cache', label: 'No Cache', hint: 'Never cache content from the origin regardless of cache headers.' },
  { key: 'ignore_query_string', label: 'Ignore Query String', hint: 'Ignore all URL query strings when caching an object.' },
];

const splitValues = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

const summariseMatch = (label: string, m?: WaapCacheMatch | null) =>
  m && m.patterns.length ? `${label} ${m.operator} [${m.patterns.join(', ')}]` : '';

const matchSummary = (r: WaapCacheRule) =>
  [summariseMatch('URL Path', r.match_url_path), summariseMatch('Query String', r.match_query_string)]
    .filter(Boolean)
    .join('; ') || '—';

interface FormState {
  name: string;
  what: string;
  operator: string;
  values: string;
  ttl: string;
  priority: string;
  enable_x_cache: boolean;
  ignore_origin_no_cache: boolean;
  ignore_client_no_cache: boolean;
  never_cache: boolean;
  ignore_query_string: boolean;
  vary_mimes: string[];
}

const EMPTY_FORM: FormState = {
  name: '',
  what: URL_PATH,
  operator: 'equals',
  values: '',
  ttl: '',
  priority: '',
  enable_x_cache: false,
  ignore_origin_no_cache: false,
  ignore_client_no_cache: false,
  never_cache: false,
  ignore_query_string: false,
  vary_mimes: [],
};

/**
 * Cache Control — website-detail tab. Cache rules keyed by a URL-path / query-
 * string match, backed by the PUBLIC Open-API (root conversant SDK, HMAC) via
 * /cache. Website fixed by the detail route (`:domainId`).
 */
export function CacheControlTab() {
  const { domainId } = useParams<{ domainId: string }>();
  const { show } = useToast();
  const { data, isLoading } = useWaapCacheRules(domainId);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editing, setEditing] = useState<WaapCacheRule | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WaapCacheRule | null>(null);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const closeForm = () => {
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };
  const startEdit = (r: WaapCacheRule) => {
    setEditing(r);
    // The public surface stores one slot at a time; prefer URL path when set.
    const active = r.match_url_path ?? r.match_query_string ?? null;
    setForm({
      name: r.name,
      what: r.match_query_string && !r.match_url_path ? QUERY_STRING : URL_PATH,
      operator: active?.operator ?? 'equals',
      values: (active?.patterns ?? []).join(', '),
      ttl: r.ttl ? String(r.ttl) : '',
      priority: r.priority ? String(r.priority) : '',
      enable_x_cache: r.enable_x_cache,
      ignore_origin_no_cache: r.ignore_origin_no_cache,
      ignore_client_no_cache: r.ignore_client_no_cache,
      never_cache: r.never_cache,
      ignore_query_string: r.ignore_query_string,
      vary_mimes: r.vary_mimes ?? [],
    });
    setOpen(true);
  };

  const create = useCreateWaapCacheRule(domainId ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Cache policy created' });
      closeForm();
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateWaapCacheRule(domainId ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Cache policy updated' });
      closeForm();
    },
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const toggle = useToggleWaapCacheRule(domainId ?? '', {
    onSuccess: () => show({ tone: 'success', title: 'Cache policy updated' }),
    onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }),
  });
  const remove = useDeleteWaapCacheRule(domainId ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Cache policy deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const toggleMime = (m: string) =>
    set('vary_mimes', form.vary_mimes.includes(m) ? form.vary_mimes.filter((x) => x !== m) : [...form.vary_mimes, m]);

  const valid = form.name.trim() && splitValues(form.values).length > 0;

  // The vendor rejects priority 0 ("Priority is empty or invalid"), so an empty
  // field defaults to max-existing + 1 (edits keep their current priority).
  const nextPriority = () => Math.max(0, ...(data ?? []).map((r) => r.priority ?? 0)) + 1;

  const submit = () => {
    const match: WaapCacheMatch = { operator: form.operator, patterns: splitValues(form.values) };
    const body: WaapCacheInput = {
      name: form.name.trim(),
      priority: Number(form.priority) > 0 ? Number(form.priority) : editing?.priority || nextPriority(),
      enabled: editing?.enabled ?? true,
      ttl: Number(form.ttl) || 0,
      ignore_client_no_cache: form.ignore_client_no_cache,
      ignore_origin_no_cache: form.ignore_origin_no_cache,
      ignore_query_string: form.ignore_query_string,
      enable_x_cache: form.enable_x_cache,
      never_cache: form.never_cache,
      vary_mimes: form.vary_mimes,
      match_url_path: form.what === URL_PATH ? match : null,
      match_query_string: form.what === QUERY_STRING ? match : null,
    };
    if (editing) update.mutate({ id: editing.id, body });
    else create.mutate(body);
  };
  const saving = create.isPending || update.isPending;

  if (isLoading) return <Skeleton className={styles.skeleton} />;

  const rules = data ?? [];

  return (
    <Section
      title="Cache Control"
      description="Cache rules matched by URL path or query string, with TTL and Vary controls."
    >
      <Card>
        <CardHeader
          actions={
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={14} />}
              onClick={() => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); }}
            >
              Add Cache
            </Button>
          }
        >
          <CardTitle>Cache Policies</CardTitle>
        </CardHeader>
        <CardBody className={styles.bodyGrid}>
          {rules.length === 0 ? (
            <EmptyState icon={<Layers size={20} />} title="No cache policies yet" description="Add a cache policy to control TTL and caching behavior for this website." />
          ) : (
            <div className={styles.rulesScroll}><div className={styles.rules}>
              {rules.map((r) => (
                <div key={r.id} className={styles.ruleRow}>
                  <Badge tone="neutral" appearance="subtle" size="sm">{r.priority}</Badge>
                  <span className={styles.name}>{r.name}</span>
                  <span className={styles.muted}>{matchSummary(r)}</span>
                  <span className={styles.muted}>{r.never_cache ? 'No cache' : `TTL ${r.ttl || '—'}`}</span>
                  <Badge tone={r.enabled ? 'success' : 'neutral'} appearance="subtle" size="sm">
                    {r.enabled ? 'Normal' : 'Disabled'}
                  </Badge>
                  <div className={styles.actions}>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(r)}>
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={toggle.isPending}
                      onClick={() => toggle.mutate(r)}
                    >
                      {r.enabled ? 'Disable' : 'Enable'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPendingDelete(r)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div></div>
          )}

        </CardBody>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { if (!o) closeForm(); }}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Cache Policy' : 'Add Cache Policy'}</DialogTitle>
            <DialogDescription>
              Configure match conditions, TTL, cache toggles, and Vary image types for this cache policy.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className={styles.dialogForm}>
              <Field label="Policy name" required>
                {(p) => <Input {...p} value={form.name} placeholder="new-policy-cache" onChange={(e) => set('name', e.target.value)} />}
              </Field>

              <Field label="When incoming requests match" description="Field · operator · values (comma-separated). Operators: equals (exact), prefix (starts with), suffix (ends with), regex (regular expression).">
                {() => (
                  <div className={styles.matchRow}>
                    <Select value={form.what} onValueChange={(v) => set('what', v)}>
                      <SelectTrigger className={styles.fieldSelect}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FIELDS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={form.operator} onValueChange={(v) => set('operator', v)}>
                      <SelectTrigger className={styles.opSelect}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className={styles.grow} placeholder="/cache" value={form.values} onChange={(e) => set('values', e.target.value)} />
                  </div>
                )}
              </Field>

              <div className={styles.grid2}>
                <Field label="TTL (seconds)" description="Cache lifetime. Empty = use origin cache TTL.">
                  {(p) => <Input {...p} type="number" placeholder="Default: origin cache TTL" value={form.ttl} onChange={(e) => set('ttl', e.target.value)} />}
                </Field>
                <Field label="Priority" description="Higher number = higher precedence.">
                  {(p) => <Input {...p} type="number" value={form.priority} onChange={(e) => set('priority', e.target.value)} />}
                </Field>
              </div>

              <div className={styles.toggleList}>
                {TOGGLES.map((t) => (
                  <label key={t.key} className={styles.toggleLabel}>
                    <Checkbox checked={form[t.key]} onCheckedChange={(v) => set(t.key, Boolean(v))} />
                    <span>
                      {t.label}
                      <span className={styles.toggleHint}>{t.hint}</span>
                    </span>
                  </label>
                ))}
              </div>

              <Field label="Vary for Images" description="Serve compatible image types per user agent via the Vary response header.">
                {() => (
                  <div className={styles.varyRow}>
                    {VARY_MIMES.map((m) => (
                      <label key={m} className={styles.checkLabel}>
                        <Checkbox checked={form.vary_mimes.includes(m)} onCheckedChange={() => toggleMime(m)} />
                        {m}
                      </label>
                    ))}
                  </div>
                )}
              </Field>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button disabled={!valid || saving} onClick={submit}>
              {saving ? 'Saving…' : editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete cache policy?"
        description={`Remove "${pendingDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </Section>
  );
}
